import { type TextToPayBeginPaymentResponse } from '@m/types/api';

export type PayAttemptOutcome<T> =
  | { begin: TextToPayBeginPaymentResponse; kind: 'amountChanged' }
  | { error: unknown; kind: 'beginFailed' }
  | { kind: 'confirmed'; result: T }
  | { kind: 'noPaymentRequired' };

export type ExpectedAmount = {
  /** Smallest currency unit, e.g. cents. */
  amountMinor: number;
  /** ISO 4217 code, compared case-insensitively. */
  currency: string;
};

/**
 * One pay attempt on the client-secret (Payment Element / Express Checkout) path.
 *
 * `beginPayment` is called exactly once, and `confirm` only runs after it reports `ready` for the
 * exact amount the consumer is looking at (`expected`). A `noPaymentRequired` response, a finalized
 * amount that differs from `expected`, or a failed begin call (409 bill/PaymentIntent mismatch or
 * payment already in progress, 404, network) never reaches `confirm`.
 */
export async function runPayAttempt<T>({
  beginPayment,
  confirm,
  expected,
}: {
  beginPayment: () => Promise<TextToPayBeginPaymentResponse>;
  confirm: () => Promise<T>;
  expected: ExpectedAmount;
}): Promise<PayAttemptOutcome<T>> {
  let begin: TextToPayBeginPaymentResponse;
  try {
    begin = await beginPayment();
  } catch (error: unknown) {
    return { kind: 'beginFailed', error };
  }

  if (begin.status === 'noPaymentRequired') {
    return { kind: 'noPaymentRequired' };
  }

  // Fail closed: only an explicit `ready` may reach confirm.
  if (begin.status !== 'ready') {
    return { kind: 'beginFailed', error: new Error('Unexpected begin-payment response') };
  }

  // The bill is finalized at begin time, so it can differ from the total rendered before the tap.
  // Never charge an amount the consumer has not seen.
  if (
    begin.amountMinor !== expected.amountMinor ||
    begin.currency.toUpperCase() !== expected.currency.toUpperCase()
  ) {
    return { kind: 'amountChanged', begin };
  }

  return { kind: 'confirmed', result: await confirm() };
}
